/**
 * The dashboard (M31, ADR-0042): each role's figures, applications per
 * month and by status, and the work to pick up next.
 */
import {
  BadgeCheck,
  BookOpenCheck,
  ClipboardCheck,
  Eye,
  FilePen,
  FilePlus2,
  FolderKanban,
  Gavel,
  Globe2,
  Hourglass,
  Inbox,
  Landmark,
  ListChecks,
  type LucideIcon,
  Timer,
  Undo2,
  Users,
  UsersRound,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { ApplicationSummary } from "@/api/applications";
import { type Dashboard, useDashboard } from "@/api/dashboard";
import { currentRole, useSession } from "@/auth/session";
import { MonthlyChart, StatusChart } from "@/components/charts";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { CardsSkeleton, Empty, LoadFailed } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { columnsOf } from "@/components/ui/data-table-columns";
import { formatDateTime, formatNumber } from "@/lib/format";
import { StatusBadge } from "@/pages/applications/parts";

interface Figure {
  key: string;
  icon: LucideIcon;
  to?: string;
  days?: boolean;
}

const FIGURES: Record<Dashboard["role"], Figure[]> = {
  ARCHITECT: [
    { key: "preChecks", icon: ClipboardCheck, to: "/pre-checks" },
    { key: "drafts", icon: FilePen, to: "/applications" },
    { key: "inReview", icon: Hourglass, to: "/applications" },
    { key: "returned", icon: Undo2, to: "/applications" },
    { key: "approved", icon: BadgeCheck, to: "/applications" },
  ],
  OFFICER: [
    { key: "waiting", icon: Inbox, to: "/review" },
    { key: "underReview", icon: Eye, to: "/review" },
    { key: "decidedThisMonth", icon: Gavel },
    { key: "medianDaysToDecision", icon: Timer, days: true },
  ],
  ADMIN: [
    { key: "members", icon: Users, to: "/admin/members" },
    { key: "ruleSetsPublished", icon: BookOpenCheck, to: "/admin/rule-sets" },
    { key: "submittedThisMonth", icon: FolderKanban },
    { key: "approvedThisMonth", icon: BadgeCheck },
  ],
  PLATFORM: [
    { key: "states", icon: Globe2, to: "/platform/states" },
    { key: "authorities", icon: Landmark, to: "/platform/authorities" },
    { key: "accounts", icon: UsersRound, to: "/platform/accounts" },
    { key: "applications", icon: FolderKanban },
  ],
};

const column = columnsOf<ApplicationSummary>();

function RecentApplications({ items, officer }: { items: ApplicationSummary[]; officer: boolean }) {
  const { t } = useTranslation();
  const columns = [
    column.accessor((item) => item.number ?? t("command.draft"), {
      id: "number",
      header: t("dashboard.columns.application"),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <Link to={officer ? `/review/${row.original.id}` : `/applications/${row.original.id}`} className="font-medium hover:text-primary hover:underline">
            {row.original.number ?? t("command.draft")}
          </Link>
          <span className="text-xs text-muted-foreground">{row.original.ownerName}</span>
        </div>
      ),
    }),
    column.accessor("siteAddress", {
      meta: { className: "hidden md:table-cell" },
      header: t("dashboard.columns.site"),
      cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span>,
    }),
    column.accessor("status", {
      header: t("dashboard.columns.status"),
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    }),
    column.accessor("updatedAt", {
      header: t("dashboard.columns.updated"),
      meta: { className: "hidden text-right sm:table-cell" },
      cell: ({ getValue }) => <span className="whitespace-nowrap tabular-nums text-muted-foreground">{formatDateTime(getValue())}</span>,
    }),
  ];
  return (
    <DataTable
      data={items}
      columns={columns}
      caption={t(officer ? "dashboard.waitingTitle" : "dashboard.recentTitle")}
      paginate={false}
      sortable={false}
      getRowId={(item) => item.id}
      empty={<Empty title={t(officer ? "dashboard.noneWaiting" : "dashboard.noneYet")} />}
    />
  );
} // prettier-ignore

function Actions({ role }: { role: string | null }) {
  const { t } = useTranslation();
  if (role === "ARCHITECT") {
    return (
      <>
        <Button asChild variant="secondary">
          <Link to="/applications/new">
            <FolderKanban aria-hidden="true" className="size-4" />
            {t("application.new")}
          </Link>
        </Button>
        <Button asChild>
          <Link to="/pre-checks/new">
            <FilePlus2 aria-hidden="true" className="size-4" />
            {t("home.startCheck")}
          </Link>
        </Button>
      </>
    );
  }
  if (role === "OFFICER") {
    return (
      <Button asChild>
        <Link to="/review">
          <ListChecks aria-hidden="true" className="size-4" />
          {t("home.openQueue")}
        </Link>
      </Button>
    );
  }
  return null;
}

/** The landing page: each role's dashboard. */
export function HomePage() {
  const { t } = useTranslation();
  const session = useSession();
  const dashboard = useDashboard();
  if (!session.data) {
    return null;
  }
  const role = currentRole(session.data);
  const intro = role === "ARCHITECT" ? "home.architectIntro" : role === "OFFICER" ? "home.officerIntro" : role === "ADMIN" ? "home.adminIntro" : "home.platformIntro";
  const data = dashboard.data;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("home.title", { name: session.data.user.fullName })}
        description={t(intro)}
        actions={<Actions role={role} />}
      />
      {dashboard.isPending ? (
        <CardsSkeleton />
      ) : dashboard.isError || !data ? (
        <LoadFailed error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      ) : (
        <>
          <section aria-label={t("dashboard.figures")} className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
            {FIGURES[data.role].filter((figure) => figure.key in data.figures).slice(0, 4).map(({ key, icon, to, days }) => {
              const value = data.figures[key];
              return (
                <StatCard
                  key={key}
                  label={t(`dashboard.figure.${key}`)}
                  value={value === null || value === undefined ? "—" : days ? (value < 1 ? t("dashboard.lessThanADay") : t("dashboard.days", { count: Math.round(value) })) : formatNumber(value, 0)}
                  note={t(`dashboard.note.${key}`, { defaultValue: "" }) || undefined}
                  icon={icon}
                  to={to}
                />
              );
            })}
          </section>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2">
              <CardHeader title={t("dashboard.monthly")} description={t("dashboard.monthlyIntro")} />
              <MonthlyChart months={data.perMonth} />
            </Card>
            <Card>
              <CardHeader title={t("dashboard.byStatus")} description={t(data.role === "ARCHITECT" ? "dashboard.byStatusMine" : "dashboard.byStatusAll")} />
              {data.byStatus.length > 0 ? <StatusChart counts={data.byStatus} /> : <p className="text-sm text-muted-foreground">{t("dashboard.noneYet")}</p>}
            </Card>
          </div>
          {data.role === "ARCHITECT" || data.role === "OFFICER" ? (
            <Card>
              <CardHeader
                title={t(data.role === "OFFICER" ? "dashboard.waitingTitle" : "dashboard.recentTitle")}
                actions={
                  <Button asChild variant="ghost" size="sm">
                    <Link to={data.role === "OFFICER" ? "/review" : "/applications"}>{t("dashboard.seeAll")}</Link>
                  </Button>
                }
              />
              <RecentApplications items={data.recent} officer={data.role === "OFFICER"} />
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
} // prettier-ignore

export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-4 py-12">
      <p className="text-sm font-medium text-primary">404</p>
      <h1 className="text-3xl font-semibold tracking-tight">{t("states.notFoundTitle")}</h1>
      <p className="text-muted-foreground">{t("states.notFoundBody")}</p>
      <Button asChild>
        <a href="/">{t("states.goHome")}</a>
      </Button>
    </div>
  );
}
